import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// A move opens a NEW enquiry (type Move) with its own J reference, linked to this booking with the reason.
// The original stay is NOT cancelled here - that remains the explicit cancellation request on this booking.
export function MoveDialog({ booking, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ reason: "", arrival_date: "", departure_date: "", location_text: "", postcode: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workflow/moves", { original_booking_id: booking.id, reason: form.reason, arrival_date: form.arrival_date, departure_date: form.departure_date,
        location_text: form.location_text || null, postcode: form.postcode || null, rooms_required: booking.total_rooms || null, guests_count: booking.total_guests || null });
      toast.success(`Move enquiry ${r.data.jigsaw_reference} created — original stay unchanged`);
      onOpenChange(false); onCreated?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the move"); }
    finally { setBusy(false); }
  };
  const valid = form.reason.trim() && form.arrival_date && form.departure_date && form.departure_date > form.arrival_date;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle>Raise a move from {booking.booking_reference}</DialogTitle>
          <DialogDescription>Creates a new enquiry with its own reference, linked to this booking. The current stay stays live until a cancellation request on this booking is decided.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="move-reason">Move reason</Label><Textarea id="move-reason" data-testid="move-reason" rows={2} value={form.reason} onChange={set("reason")} placeholder="Why the guest is moving" /></div>
          <div className="space-y-1.5"><Label htmlFor="move-arrival">New arrival</Label><Input id="move-arrival" data-testid="move-arrival" type="date" value={form.arrival_date} onChange={set("arrival_date")} /></div>
          <div className="space-y-1.5"><Label htmlFor="move-departure">New departure</Label><Input id="move-departure" data-testid="move-departure" type="date" value={form.departure_date} onChange={set("departure_date")} /></div>
          <div className="space-y-1.5"><Label htmlFor="move-location">Area / location</Label><Input id="move-location" data-testid="move-location" value={form.location_text} onChange={set("location_text")} /></div>
          <div className="space-y-1.5"><Label htmlFor="move-postcode">Postcode</Label><Input id="move-postcode" data-testid="move-postcode" value={form.postcode} onChange={set("postcode")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="move-cancel">Cancel</Button>
          <Button disabled={!valid || busy} onClick={submit} data-testid="move-submit">Create move enquiry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
